import React, { useState, useEffect } from 'react';
import { Trash2, Download, Image, Calendar, ChevronDown, ChevronUp, Cloud, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { amplifyApi } from '../services/amplifyApi';
import awsExports from '../aws-exports';

// Helper to get API endpoint from aws-exports (supports multi-env)
const getApiEndpoint = () => awsExports.aws_cloud_logic_custom?.[0]?.endpoint || 'https://53ogiynuu2.execute-api.us-east-1.amazonaws.com/dev';

const SavedDesigns = ({ onLoadDesign }) => {
  const { isAuthenticated } = useAuth();
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadSavedDesigns();
    } else {
      setSavedDesigns([]);
    }
  }, [isAuthenticated]);

  const loadSavedDesigns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await amplifyApi.getSavedDesigns();
      if (response.success) {
        setSavedDesigns(response.designs || []);
      } else {
        setError(response.error || 'Failed to load designs');
      }
    } catch (err) {
      console.error('Failed to load saved designs:', err);
      setError('Failed to load designs');
      setSavedDesigns([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (designId) => {
    if (!window.confirm('Delete this saved design?')) return;

    try {
      const response = await amplifyApi.deleteDesign(designId);
      if (response.success) {
        setSavedDesigns(savedDesigns.filter(d => d.id !== designId));
      } else {
        alert(response.error || 'Failed to delete design');
      }
    } catch (err) {
      console.error('Failed to delete design:', err);
      alert('Failed to delete design');
    }
  };

  const handleDownload = async (design) => {
    const filename = `${design.name || 'design'}.png`;

    try {
      // For external URLs (like S3), fetch and create a blob to enable proper download
      if (design.url && (design.url.startsWith('http://') || design.url.startsWith('https://'))) {
        // Check if URL needs to go through proxy (CORS issues)
        const needsProxy = design.url.includes('d38hhp6342myl9.cloudfront.net') || // World Illustrated CDN
                          design.url.includes('pfy-prod-image-storage.s3') ||
                          design.url.includes('images-api.printify');

        let fetchUrl = design.url;
        if (needsProxy) {
          const apiEndpoint = getApiEndpoint();
          const cacheBuster = Date.now();
          fetchUrl = `${apiEndpoint}/api/images/proxy?dataurl=true&_=${cacheBuster}&url=${encodeURIComponent(design.url)}`;
        }

        const response = await fetch(fetchUrl);

        if (needsProxy) {
          // Proxy returns JSON with dataUrl
          const result = await response.json();
          if (result.success && result.dataUrl) {
            const link = document.createElement('a');
            link.download = filename;
            link.href = result.dataUrl;
            link.click();
          } else {
            throw new Error(result.error || 'Failed to get image from proxy');
          }
        } else {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.download = filename;
          link.href = blobUrl;
          link.click();

          // Clean up blob URL after download
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        }
      } else {
        // For data URLs, direct download works
        const link = document.createElement('a');
        link.download = filename;
        link.href = design.url;
        link.click();
      }
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in new tab
      window.open(design.url, '_blank');
    }
  };

  const handleLoad = (design) => {
    if (onLoadDesign) {
      onLoadDesign(design);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Loading state
  if (isLoading && savedDesigns.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-center gap-3 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading saved designs...</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (savedDesigns.length === 0 && !error) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Cloud className="w-5 h-5 text-blue-500" />
          <span className="font-medium text-slate-900">Saved Designs</span>
          <span className="bg-slate-100 text-slate-600 text-xs font-medium px-2 py-0.5 rounded-full">
            {savedDesigns.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>

      {/* Refresh button - visible when expanded */}
      {isExpanded && (
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              loadSavedDesigns();
            }}
            disabled={isLoading}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 transition-colors disabled:opacity-50"
            title="Refresh saved designs"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      )}

      {/* Error message */}
      {error && isExpanded && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-sm border-t border-red-100">
          {error}
          <button
            onClick={loadSavedDesigns}
            className="ml-2 underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Expanded content */}
      {isExpanded && savedDesigns.length > 0 && (
        <div className="border-t border-slate-200 p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {savedDesigns.map((design) => (
              <div
                key={design.id}
                className="group relative bg-slate-50 rounded-lg overflow-hidden border border-slate-200 hover:border-blue-300 transition-colors"
              >
                {/* Thumbnail */}
                <div className="aspect-square relative">
                  {design.url ? (
                    <img
                      src={design.url}
                      alt={design.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="12">No Preview</text></svg>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                      <Image className="w-8 h-8 text-slate-300" />
                    </div>
                  )}

                  {/* Hover overlay with actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {onLoadDesign && (
                      <button
                        onClick={() => handleLoad(design)}
                        className="p-2 bg-white rounded-full text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Use this design"
                      >
                        <Image className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(design)}
                      className="p-2 bg-white rounded-full text-slate-600 hover:bg-slate-50 transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(design.id)}
                      className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-2">
                  <p className="text-xs font-medium text-slate-700 truncate" title={design.name}>
                    {design.name}
                  </p>
                  <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" />
                    {formatDate(design.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
};

export default SavedDesigns;
